import { hasFeature } from '@precert/has-feature';
import { Lookups } from '@precert/validation';
import { createColumnHelper } from '@tanstack/react-table';
import cx from 'classnames';
import { format } from 'date-fns';
import React from 'react';
import type { DefaultExtensionType, FileIconProps } from 'react-file-icon';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { useFormContext } from 'react-hook-form';
import { Row, Col, Card, CardTitle, CardBody } from 'reactstrap';
import * as yup from 'yup';

import { useConfig } from '../../../../_components/config';
import Grid from '../../../../_components/grid';
import type { WizardStepProps } from '../wizard';

import type { AttachmentFormData } from './step-clinical-documents';
import { formatBytes } from './step-clinical-documents';
import { TypeOfCare } from './step-new-request';
import styles from './step-review.module.scss';

const { GenderOptions } = Lookups;
export interface StepSummaryProps {
  index: number;
}

export function StepSummary() {
  const { getValues } = useFormContext();
  const { features } = useConfig();
  const datum = (label: string, value: any) => (
    <>
      <div className="fw-bold">{label}</div>
      <div className="text-break">{value}</div>
    </>
  );
  const getLabel = (name: string) => (getValues(name) ? 'Yes' : 'No');
  const address = (name: string) =>
    getValues(name) ? (
      <>
        <div>{getValues(`${name}.street`)}</div>
        {getValues(`${name}.street2`) && (
          <div>{getValues(`${name}.street2`)}</div>
        )}
        <div>
          {getValues(`${name}.city`)}, {getValues(`${name}.state`)}{' '}
          {getValues(`${name}.zip`)}
        </div>
      </>
    ) : (
      ''
    );
  const commaSeparatedList = (name: string) => getValues(name)?.join(', ');
  const columnHelper = createColumnHelper<AttachmentFormData>();
  const attachments = getValues('attachments') || [];

  return (
    <div className={styles.review}>
      <Row>
        <Col lg={6} className="mt-0">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Notification Details</h2>
            </CardTitle>
            <CardBody className="ps-3">
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum(
                    'Veteran Referral ID Number',
                    getValues('veteranReferralId')
                  )}
                </Col>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Veteran ICN', getValues('veteranIcn'))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  {hasFeature('procedure-codes-list', features)
                    ? datum(
                        'Procedure Code(s)',
                        commaSeparatedList('procedureCodesList')
                      )
                    : datum('Procedure Code', getValues('procedureCode'))}
                </Col>
                <Col md={6}>
                  {datum('Type of Care', TypeOfCare[getValues('requestType')])}
                </Col>
              </Row>
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Episodic Care', getLabel('episodicCare'))}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col lg={6} className="mt-3 mt-lg-0">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Veteran Demographics</h2>
            </CardTitle>
            <CardBody className="ps-3">
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('First Name', getValues('veteranFirstName'))}
                </Col>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Last Name', getValues('veteranLastName'))}
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-md-3 mb-1">
                  {datum('Gender', GenderOptions[getValues('veteranGender')])}
                </Col>
                <Col md={6} className="mb-md-3 mb-1">
                  {datum(
                    'Date of Birth',
                    getValues('veteranDob') &&
                      format(getValues('veteranDob'), 'MM/dd/yyyy')
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} className="mt-3 mt-lg-4">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Precertification Details</h2>
            </CardTitle>
            <CardBody className="ps-3">
              <Row className="mb-md-3 mb-0">
                <Col md={6}>
                  {datum('Body Location', getValues('bodyLocation'))}
                </Col>
                <Col md={6}>
                  {datum('Place of Service', getValues('placeOfService'))}
                </Col>
              </Row>
              <Row className="mb-md-3 mb-0">
                <Col md={6}>{datum('Quantity', getValues('quantity'))}</Col>
                <Col md={6}>
                  {datum('Quantity Type', getValues('quantityType'))}
                </Col>
              </Row>
              <Row className="mb-md-3 mb-0">
                <Col md={6}>
                  {datum('Service Type', getValues('serviceType'))}
                </Col>
                <Col md={6}>
                  {datum(
                    'Date of Service',
                    getValues('dateOfService') &&
                      format(getValues('dateOfService'), 'MM/dd/yyyy')
                  )}
                </Col>
              </Row>
              <Row className="mb-md-3 mb-0">
                {hasFeature('diagnosis-upgrade', features) ? (
                  <>
                    <Col md={6} className="mb-md-3 mb-0">
                      {datum(
                        'Primary Diagnosis',
                        getValues('primaryDiagnosis')
                      )}
                    </Col>
                    <Col md={6} className="mb-md-3 mb-0">
                      {datum(
                        'Secondary Diagnosis',
                        commaSeparatedList('secondaryDiagnosis')
                      )}
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={6}>
                      {datum(
                        'Diagnosis Code(s)',
                        commaSeparatedList('diagnosisCodes')
                      )}
                    </Col>
                  </>
                )}
                <Col md={6}>
                  {datum('Drug Name/Description', getValues('infusionCode'))}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} className="mt-3 mt-lg-4">
          <Card className="rounded h-100  mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Rendering Provider</h2>
            </CardTitle>
            <CardBody className="ps-3">
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Provider NPI', getValues('providerNpi'))}
                </Col>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Provider Name', getValues('providerName'))}
                </Col>
              </Row>
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Provider Type', getValues('providerType'))}
                </Col>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Specialty-Taxonomy', getValues('specialTaxonomy'))}
                </Col>
              </Row>
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Facility NPI', getValues('facilityNpi'))}
                </Col>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Facility Name', getValues('facilityName'))}
                </Col>
              </Row>
              <Row>
                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Facility Address', address('facilityAddress'))}
                </Col>

                <Col className="mb-md-3 mb-1" md={6}>
                  {datum('Secondary Address', address('secondaryAddress'))}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col lg={6} className="mt-3 mt-lg-4">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Clinical Documents</h2>
            </CardTitle>
            <CardBody className="ps-3 pe-3 pb-3">
              {attachments.length > 0 && (
                <div className="mx-3 mb-3">
                  <Grid
                    data={attachments}
                    className={styles.clinicalDocumentsReviewGrid}
                    columns={[
                      columnHelper.accessor('fileName', {
                        header: 'File Name',
                        id: 'fileName',
                        cell: (info) => {
                          const ext = info.getValue().match(/\w+$/);
                          return (
                            <>
                              <div
                                style={{
                                  width: '18px',
                                  height: '5px',
                                  paddingTop: '3px',
                                }}
                              >
                                <FileIcon
                                  color="#C7C7C7"
                                  labelColor="blue"
                                  extension={ext}
                                  labelUppercase
                                  {...defaultStyles[
                                    ext as keyof Record<
                                      DefaultExtensionType,
                                      Partial<FileIconProps>
                                    >
                                  ]}
                                  radius={2}
                                />
                              </div>
                              <span className="ms-4">{info.getValue()}</span>
                            </>
                          );
                        },
                      }),
                      columnHelper.accessor('contentLength', {
                        header: 'File Length',
                        cell: (info) => formatBytes(info.getValue()),
                      }),
                    ]}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} className="mt-3 mt-lg-4">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Point of Contact</h2>
            </CardTitle>
            <CardBody className="ps-3">
              <Row className="mb-md-3 mb-0">
                <Col md={6}>
                  {datum('First Name', getValues('pointOfContactFirstName'))}
                </Col>
                <Col md={6}>
                  {datum('Last Name', getValues('pointOfContactLastName'))}
                </Col>
              </Row>
              <Row className="mb-md-3 mb-0">
                <Col md={6}>
                  {datum('Phone', getValues('pointOfContactPhone'))}
                </Col>
                <Col md={6}>{datum('Fax', getValues('pointOfContactFax'))}</Col>
              </Row>
              <Row className="mb-md-3 mb-0">
                <Col md={6}>
                  {datum('Email', getValues('pointOfContactEmail'))}
                </Col>
              </Row>
              {getValues('hasAlternatePointOfContact') && (
                <>
                  <h3 className="fw-bold pt-3">Alternate Point of Contact</h3>
                  <Row className="mb-md-3 mb-0">
                    <Col md={6}>
                      {datum(
                        'First Name',
                        getValues('alternatePointOfContactFirstName')
                      )}
                    </Col>
                    <Col md={6}>
                      {datum(
                        'Last Name',
                        getValues('alternatePointOfContactLastName')
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-md-3 mb-0">
                    <Col md={6}>
                      {datum(
                        'Phone',
                        getValues('alternatePointOfContactPhone')
                      )}
                    </Col>
                    <Col md={6}>
                      {datum('Fax', getValues('alternatePointOfContactFax'))}
                    </Col>
                  </Row>
                  <Row className="mb-md-3 mb-0">
                    <Col md={6}>
                      {datum(
                        'Email',
                        getValues('alternatePointOfContactEmail')
                      )}
                    </Col>
                  </Row>{' '}
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={12} className="mt-3 mt-lg-4">
          <Card className="rounded h-100 mt-3">
            <CardTitle>
              <h2 className="fw-bold ps-3 pt-3">Clinical Notes</h2>
            </CardTitle>
            <div className={cx(styles.clinicalNotes, 'ps-3 pe-3 pb-3')}>
              {getValues('clinicalNotes')}
            </div>
          </Card>
        </Col>
      </Row>
      <br />
      <br />
      CPT copyright 2024 American Medical Association. All rights reserved.
    </div>
  );
}

export const stepSummary: WizardStepProps = {
  title: 'Review and Submit',
  schema: yup.object(),
  element: <StepSummary />,
  path: 'review-and-submit',
};
